import React from 'react';
import {useForm, SubmitHandler, Resolver, FieldErrors} from 'react-hook-form';
import {olzApi, OlzMetaData, OlzKarteData, OlzKarteKind} from '../../../Api/client';
import {initOlzEditModal, OlzEditModal, OlzEditModalStatus} from '../../../Common/Components/OlzEditModal/OlzEditModal';
import {OlzTextField} from '../../../Common/Components/OlzTextField/OlzTextField';
import {OlzImageField} from '../../../Components/Upload/OlzImageField/OlzImageField';
import {OlzLocationField, deserializeLocation, serializeLocation, validateLocation} from '../../../Common/Components/OlzLocationField/OlzLocationField';
import {getApiNumber, getApiString, getFormNumber, getFormString, getResolverResult, validateIntegerOrNull, validateNotEmpty} from '../../../Utils/formUtils';
import {assert} from '../../../Utils/generalUtils';

import './OlzEditKarteModal.scss';

interface OlzEditKarteForm {
    kartenNr: string,
    name: string,
    location: string,
    year: string,
    scale: string,
    place: string,
    zoom: string,
    kind: string,
    previewImageId: string,
}

const resolver: Resolver<OlzEditKarteForm> = async (values) => {
    const errors: FieldErrors<OlzEditKarteForm> = {};
    errors.kartenNr = validateIntegerOrNull(values.kartenNr);
    errors.name = validateNotEmpty(values.name);
    errors.location = validateLocation(values.location);
    errors.year = validateIntegerOrNull(values.year);
    errors.zoom = validateIntegerOrNull(values.zoom);
    return getResolverResult(errors, values);
};

function getFormFromApi(apiData?: OlzKarteData): OlzEditKarteForm {
    return {
        kartenNr: getFormNumber(apiData?.kartennr),
        name: getFormString(apiData?.name),
        location: apiData?.location ? serializeLocation(apiData?.location) : '',
        year: getFormNumber(apiData?.year),
        scale: getFormString(apiData?.scale),
        place: getFormString(apiData?.place),
        zoom: getFormNumber(apiData?.zoom),
        kind: getFormString(apiData?.kind),
        previewImageId: getFormString(apiData?.previewImageId),
    };
}

function getApiFromForm(formData: OlzEditKarteForm): OlzKarteData {
    const kindByString: {[value: string]: OlzKarteKind} = {
        ol: 'ol',
        stadt: 'stadt',
        scool: 'scool',
    };
    return {
        kartennr: getApiNumber(formData?.kartenNr),
        name: getApiString(formData?.name) ?? '',
        location: deserializeLocation(formData.location),
        year: getApiNumber(formData?.year),
        scale: getApiString(formData?.scale),
        place: getApiString(formData?.place),
        zoom: getApiNumber(formData?.zoom),
        kind: kindByString?.[formData?.kind ?? 'ol'] ?? 'ol',
        previewImageId: getApiString(formData?.previewImageId),
    };
}

// ---

interface OlzEditKarteModalProps {
    id?: number;
    meta?: OlzMetaData;
    data?: OlzKarteData;
}

export const OlzEditKarteModal = (props: OlzEditKarteModalProps): React.ReactElement => {
    const {register, handleSubmit, formState: {errors}, control} = useForm<OlzEditKarteForm>({
        resolver,
        defaultValues: getFormFromApi(props.data),
    });

    const [status, setStatus] = React.useState<OlzEditModalStatus>({id: 'IDLE'});
    const [isImageLoading, setIsImageLoading] = React.useState<boolean>(false);

    const onSubmit: SubmitHandler<OlzEditKarteForm> = async (values) => {
        setStatus({id: 'SUBMITTING'});
        const meta: OlzMetaData = props?.meta ?? {
            ownerUserId: null,
            ownerRoleId: null,
            onOff: true,
        };
        const data = getApiFromForm(values);

        const [err, response] = await (props.id
            ? olzApi.getResult('updateKarte', {id: props.id, meta, data})
            : olzApi.getResult('createKarte', {meta, data}));
        if (err) {
            setStatus({id: 'SUBMIT_FAILED', message: `Anfrage fehlgeschlagen: ${JSON.stringify(err || response)}`});
            return;
        }
        setStatus({id: 'SUBMITTED'});
        // This could probably be done more smoothly!
        window.location.reload();
    };

    const onDelete = props.id ? async () => {
        setStatus({id: 'DELETING'});
        const [err, response] = await olzApi.getResult('deleteKarte', {id: assert(props.id)});
        if (err) {
            setStatus({id: 'DELETE_FAILED', message: `Löschen fehlgeschlagen: ${JSON.stringify(err || response)}`});
            return;
        }
        setStatus({id: 'DELETED'});
        // This could probably be done more smoothly!
        window.location.reload();
    } : undefined;

    const dialogTitle = props.id === undefined
        ? 'Karte erstellen'
        : 'Karte bearbeiten';
    const editModalStatus: OlzEditModalStatus = isImageLoading ? {id: 'LOADING'} : status;

    return (
        <OlzEditModal
            modalId='edit-karte-modal'
            dialogTitle={dialogTitle}
            status={editModalStatus}
            onSubmit={handleSubmit(onSubmit)}
            onDelete={onDelete}
        >
            <div className='mb-3'>
                <OlzTextField
                    title='Karten-Nummer'
                    name='kartenNr'
                    errors={errors}
                    register={register}
                />
            </div>
            <div className='mb-3'>
                <OlzTextField
                    title='Name'
                    name='name'
                    errors={errors}
                    register={register}
                />
            </div>
            <div className='row'>
                <OlzLocationField
                    title='Geografische Position'
                    name='location'
                    errors={errors}
                    control={control}
                />
            </div>
            <div className='row'>
                <div className='col mb-3'>
                    <OlzTextField
                        title='Stand'
                        name='year'
                        errors={errors}
                        register={register}
                    />
                </div>
                <div className='col mb-3'>
                    <OlzTextField
                        title='Massstab'
                        name='scale'
                        errors={errors}
                        register={register}
                    />
                </div>
            </div>
            <div className='mb-3'>
                <label htmlFor='kind-container'>Typ</label>
                <div id='kind-container'>
                    <span className='kind-option'>
                        <input
                            type='radio'
                            {...register('kind')}
                            value='ol'
                            id='isKindOl-input'
                        />
                        <label htmlFor='isKindOl-input'>OL</label>
                    </span>
                    <span className='kind-option'>
                        <input
                            type='radio'
                            {...register('kind')}
                            value='stadt'
                            id='isKindStadt-input'
                        />
                        <label htmlFor='isKindStadt-input'>Stadt</label>
                    </span>
                    <span className='kind-option'>
                        <input
                            type='radio'
                            {...register('kind')}
                            value='scool'
                            id='isKindScool-input'
                        />
                        <label htmlFor='isKindScool-input'>sCOOL</label>
                    </span>
                </div>
            </div>
            <div className='row'>
                <div className='col mb-3'>
                    <OlzTextField
                        title='Ort'
                        name='place'
                        errors={errors}
                        register={register}
                    />
                </div>
                <div className='col mb-3'>
                    <OlzTextField
                        title='Zoom (z.B. 8, für sCOOL 2)'
                        name='zoom'
                        errors={errors}
                        register={register}
                    />
                </div>
            </div>
            <div id='images-upload' className='mb-3'>
                <OlzImageField
                    title='Vorschau-Bild'
                    name='previewImageId'
                    errors={errors}
                    control={control}
                    setIsLoading={setIsImageLoading}
                />
            </div>
        </OlzEditModal>
    );
};

export function initOlzEditKarteModal(
    id?: number,
    meta?: OlzMetaData,
    data?: OlzKarteData,
): boolean {
    return initOlzEditModal('edit-karte-modal', () => (
        <OlzEditKarteModal
            id={id}
            meta={meta}
            data={data}
        />
    ));
}
